import { useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router'

import { PageLevelTableWrapper, TableColumns, useTableReducer } from '@base'
import { Table as TableV2, Pill as PillV2 } from '@baseV2'
import { enquiryInputType, poStatus, strings, supplierPurchaseOrderFilter } from '@constants'
import { getAllSupplyOrder } from '@data'
import { Address } from '@data/model'
import { paths } from '@routes'
import { Spacings } from '@styles'
import { displayAmount, formatMixedQuantities } from '@utils'

// Map an SPO status to a v2 Pill colour.
const statusColor = (status) => {
	if (status === 'ACTIVE') return 'info'
	if (status === 'COMPLETED') return 'success'
	return 'neutral'
}

// SPO totals come from the API (total_pending_weight / total_allotted_weight; weight in kg) and are shown in the unit of
// the SPO's items. An SPO mixing units has no single-unit API total, so only that case sums its items per unit instead.
const formatSpoTotal = (row, totalKey, mixedItems, mixedKey) => {
	const types = new Set((row?.order_items ?? []).map((item) => item?.input_type || enquiryInputType.WEIGHT))
	if (types.size <= 1) return formatMixedQuantities([{ input_type: [...types][0], [totalKey]: row?.[totalKey] }], totalKey)
	return formatMixedQuantities(mixedItems, mixedKey)
}

const SPOListByStatus = () => {
	const navigate = useNavigate()
	const { orderStatus } = useParams()

	const [state, dispatch, actions] = useTableReducer(supplierPurchaseOrderFilter)

	const getSpoOrderTypesToFetch = () => {
		switch (orderStatus) {
			case 'all':
				return [poStatus.created, poStatus.active, poStatus.completed, poStatus.shortclosed]
			case 'active':
				return [poStatus.active]
			case 'completed':
				return [poStatus.completed]
			default:
				return []
		}
	}

	useEffect(() => {
		if (state.loading) return
		dispatch(actions.fetchTableDataProgress())
		const types = getSpoOrderTypesToFetch()
		getAllSupplyOrder(types, state.query.page, 10, state.query.filters)
			.then((response) => {
				dispatch(actions.fetchTableDataSuccess(response.data, response.count))
			})
			.catch((err) => {
				dispatch(actions.fetchTableDataFailure(err))
			})
	}, [state.query, orderStatus])

	const columns = useMemo(() => {
		const _cols = [
			TableColumns.usernameColumn({ label: strings('supplier'), key: 'supplier', mobile: true, align: 'left' }),
			{
				label: strings('po', 'number'),
				key: (row) => {
					return (
						<>
							<div>
								<b>{row.original_supplier_po_number}</b>
							</div>
							<div>
								<small>{row.supplier_po_number}</small>
							</div>
						</>
					)
				},
				align: 'left',
				mobile: true,
			},
			{
				label: strings('quote', 'type'),
				key: (row) => {
					return row?.quote_type
				},
				align: 'center',
			},
			{
				label: 'Total Pending Quantity',
				key: (row) => formatSpoTotal(row, 'total_pending_weight', row?.order_items, 'pending_weight'),
				align: 'center',
			},
			{
				label: 'Total Allotted Quantity',
				// mixed-unit fallback: allotted to orders = item quantity (allotted_weight) − what is still pending
				key: (row) =>
					formatSpoTotal(
						row,
						'total_allotted_weight',
						row?.order_items?.map((item) => ({ ...item, allotted_to_orders: (item?.allotted_weight ?? 0) - (item?.pending_weight ?? 0) })),
						'allotted_to_orders'
					),
				align: 'center',
			},
			{
				label: strings('total_quotation_amount'),
				key: (row) => {
					return displayAmount(row.order_items.reduce((total, item) => total + item.allotted_weight * item.price_per_kg, 0))
				},
				align: 'center',
			},
			{
				label: strings('logistics_quotation'),
				key: (row) => {
					let amount = ''
					if (row?.quote_type === strings('x')) {
						amount =
							row?.lumpsum_logistics_quote !== 0
								? { type: strings('lumpsum'), price: row?.lumpsum_logistics_quote }
								: { type: strings('cost/mt'), price: row?.logistics_quote_per_mt }
					}
					if (!amount) {
						return '-'
					}

					return (
						<>
							<div>
								<b>{displayAmount(amount?.price)}</b>
							</div>
							<div style={{ textTransform: 'uppercase' }}>
								<small>({amount?.type})</small>
							</div>
						</>
					)
				},
				mobile: false,
				align: 'center',
			},
			{
				label: strings('dispatch_location'),
				key: (row) => (row?.dispatch_location ? new Address(row.dispatch_location) : null),
				align: 'center',
				mobile: true,
				// City on top, state below (stacked) via an explicit flex column.
				renderer: (data) => {
					if (!data) return '-'
					if (!data.city && !data.state) return <b>{data?.pincode}</b>
					return (
						<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: Spacings.SPACING_1 }}>
							<b>{data?.city}</b>
							<small style={{ textTransform: 'uppercase' }}>{data?.state}</small>
						</div>
					)
				},
			},
		]

		if (orderStatus === 'all') {
			_cols.push({
				label: strings('po', 'status'),
				key: (data) => data,
				align: 'center',
				renderer: (data) => (
					<PillV2 color={statusColor(data?.supplier_po_status)} variant='subtle'>
						{data?.supplier_po_status ?? '--'}
					</PillV2>
				),
				mobile: true,
			})
		}
		// v2 Table enables sorting by default; keep these columns non-sortable like before.
		return _cols.map((col) => ({ sortable: false, ...col }))
	}, [orderStatus])

	const changePageHandler = (next = true) => {
		dispatch(actions.changePage(next))
	}

	const rowClickHandler = (row) => {
		navigate(paths.admin_edit_spo(row?.supplier_po_number))
	}

	return (
		<>
			<PageLevelTableWrapper>
				<TableV2
					fullHeight
					columns={columns}
					data={state.data}
					loading={state.loading}
					rowsToDisplay={10}
					totalItemCount={state.total_count}
					page={state.query.page}
					onPageChange={changePageHandler}
					onRowClick={rowClickHandler}
					emptyState={{ text: 'No SPOs found' }}
				/>
			</PageLevelTableWrapper>
		</>
	)
}

export default SPOListByStatus
